import type { Point, XY } from '@imagise/shared';
import { getCropHandlePoints, normalizeCropRect } from './crop-rect';
import { DEFAULT_CROP_PRESENTATION } from './crop-presentation';
import type { CropPresentation, CropRect } from './types';

const toXY = (point: Point | XY): XY => ({
  x: point.x,
  y: point.y,
});

export interface CropOverlayRenderOptions {
  cropRect: CropRect;
  imageToScreen(point: XY): Point | XY | undefined;
}

export class CropOverlayRenderer {
  private readonly context: CanvasRenderingContext2D | null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private presentation: CropPresentation = DEFAULT_CROP_PRESENTATION,
  ) {
    this.context = this.canvas.getContext('2d');
  }

  setPresentation(presentation: CropPresentation): void {
    this.presentation = presentation;
  }

  resize(width: number, height: number): void {
    const nextWidth = Math.max(Math.round(width), 1);
    const nextHeight = Math.max(Math.round(height), 1);

    if (this.canvas.width !== nextWidth) {
      this.canvas.width = nextWidth;
    }

    if (this.canvas.height !== nextHeight) {
      this.canvas.height = nextHeight;
    }
  }

  clear(): void {
    this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  render(options: CropOverlayRenderOptions | null): void {
    const ctx = this.context;
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (!options) {
      return;
    }

    const normalizedRect = normalizeCropRect(options.cropRect);
    if (!normalizedRect) {
      return;
    }

    const topLeft = options.imageToScreen({ x: normalizedRect.x, y: normalizedRect.y });
    const bottomRight = options.imageToScreen({
      x: normalizedRect.x + normalizedRect.width,
      y: normalizedRect.y + normalizedRect.height,
    });

    if (!topLeft || !bottomRight) {
      return;
    }

    const screenTopLeft = toXY(topLeft);
    const screenBottomRight = toXY(bottomRight);
    const rectX = screenTopLeft.x;
    const rectY = screenTopLeft.y;
    const rectWidth = screenBottomRight.x - screenTopLeft.x;
    const rectHeight = screenBottomRight.y - screenTopLeft.y;

    ctx.save();
    ctx.fillStyle = this.presentation.shadowColor;
    ctx.globalAlpha = this.presentation.shadowOpacity;
    ctx.beginPath();
    ctx.rect(0, 0, this.canvas.width, this.canvas.height);
    ctx.rect(rectX, rectY, rectWidth, rectHeight);
    ctx.fill('evenodd');
    ctx.globalAlpha = 1;

    ctx.strokeStyle = this.presentation.gridColor;
    ctx.lineWidth = this.presentation.gridWidth;
    ctx.beginPath();
    ctx.moveTo(rectX + rectWidth / 3, rectY);
    ctx.lineTo(rectX + rectWidth / 3, rectY + rectHeight);
    ctx.moveTo(rectX + (rectWidth * 2) / 3, rectY);
    ctx.lineTo(rectX + (rectWidth * 2) / 3, rectY + rectHeight);
    ctx.moveTo(rectX, rectY + rectHeight / 3);
    ctx.lineTo(rectX + rectWidth, rectY + rectHeight / 3);
    ctx.moveTo(rectX, rectY + (rectHeight * 2) / 3);
    ctx.lineTo(rectX + rectWidth, rectY + (rectHeight * 2) / 3);
    ctx.stroke();

    ctx.strokeStyle = this.presentation.borderColor;
    ctx.lineWidth = this.presentation.borderWidth;
    ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);

    const handleRadius = this.presentation.handleSize / 2;
    const handlePoints = getCropHandlePoints(normalizedRect)
      .map((point) => options.imageToScreen(point))
      .filter((point): point is Point | XY => Boolean(point))
      .map(toXY);

    ctx.fillStyle = this.presentation.handleFill;
    handlePoints.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, handleRadius, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
  }
}
